'use client';

import classnames from 'classnames';

import Icon from 'molecules/Icon';
import Image from 'molecules/Image';
import Link from 'molecules/Link';

import styles from 'components/Footer/footer.module.scss';

import { isNonEmptyArray } from 'utils/functions';

import type { FooterProps } from 'components/Footer/footer.types';
import type { FC } from 'react';

const learningCenterDomain = process.env.NEXT_PUBLIC_LEARNING_CENTER_SITE_DOMAIN || '';

type GlobalFooterProps = FooterProps['data'];

const GlobalFooter: FC<GlobalFooterProps> = ({ footerMenu = [], legals = [], socials = {} }) => (
  <footer className={styles.footerContainer}>
    <div className={styles.footerInner}>
      <nav className={styles.footerContent} aria-label="Footer menu">
        <div className={styles.footerListing}>
          <div className={styles.footerContactContainer}>
            <Icon icon="icon-office" size={20} />
            <div>
              <p className={styles.footerHeadingsAddress}> Corporate Headquarters</p>
              <div className={styles.externalLinkContainer}>
                <Icon size={32} icon="external-tooltip" className={`${styles.commonStylesExternalLinks}`} />
                <p className={styles.footerBody}>
                  <Link
                    className={styles.footerLinks}
                    rel="noopener,noreferrer"
                    id="Google maps"
                    aria-label="Navigating to MagMutual on Google Maps."
                    href="https://maps.app.goo.gl/b1cV3qq5mwWvJiPK8"
                  >
                    3550 Peachtree Road NE
                    <br />
                    Suite 500
                    <br />
                    Atlanta, GA 30326
                    <br />
                  </Link>
                  <Link className={styles.footerLinks} href="tel:800-282-4882">
                    800-282-4882
                  </Link>
                </p>
              </div>
            </div>
          </div>
          <div className={styles.footerMailContainer}>
            <Icon icon="icon-mail-footer" size={20} />
            <div className={styles.footerContactContent}>
              <p className={styles.footerHeadingsAddress}>Mailing Address</p>
              <p className={styles.footerBody}>PO Box 52979 | Atlanta, GA 30355</p>
            </div>
          </div>
        </div>
        {footerMenu?.map(({ title = '', links = [] }) => (
          <div className={styles.footerListing} key={title}>
            {title && <p className={styles.footerHeadings}>{title}</p>}
            <ul>
              {links?.map(link => {
                if (!link?.title) {
                  return null;
                }
                const isLearningCenter = link.title === 'MyMagMutual';
                const href = isLearningCenter ? learningCenterDomain : link?.link;

                return (
                  <li key={link?.title || ''}>
                    <Link className={styles.footerLinks} href={href || ''} target="_self">
                      {link.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {socials && (
          <div className={styles.footerListing}>
            {socials?.title && <p className={styles.footerHeadings}>{socials.title}</p>}
            {socials?.description && <p className={styles.footerBody}>{socials.description}</p>}
            {isNonEmptyArray(socials?.socials) && (
              <ul className={styles.footerSocial}>
                {socials?.socials.map(({ title = '', url = '', image = '' }) => (
                  <li key={title}>
                    <Link rel="noopener" id={title} aria-label={`Navigating to MagMutual's ${title}`} href={url}>
                      <span className={styles.externalLinkContainer}>
                        <Icon
                          icon="external-tooltip"
                          size={32}
                          className={`${styles.commonStylesExternalLinks} ${styles.socialMediaExternalLinks}`}
                        />
                        <Image width={20} height={20} url={image} alt={title} />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </nav>
    </div>
    <div className={styles.footerBottom}>
      <div className={styles.footerBottomInner}>
        <p className={styles.footerBody}>©{new Date().getFullYear()} MagMutual. All Rights Reserved.</p>
        {isNonEmptyArray(legals) && (
          <ul className={classnames(styles.footerBody, styles.footerTerms)}>
            {legals?.map(({ title = '', url = '' }, index) => {
              if (!title) {
                return null;
              }
              const isLast = index === legals.length - 1;
              const borderStyle = isLast ? undefined : { borderRight: '1px solid white' };

              return (
                <li key={title} style={borderStyle}>
                  <Link className={styles.footerLinks} href={url}>
                    {title}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  </footer>
);

export default GlobalFooter;
